'use client';

import React, { Suspense, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { use } from 'react';

import { isMobile } from 'react-device-detect';

import { FeatureMapList, EmptyStateView, SportContext, ThemeContext } from '@dstantwo/ullr-react-components';

import UllrLoadingIndicator from '../../../../../../src/components/ui/UllrLoadingIndicator';
import DownloadAppModal from '../../../../../../src/components/DownloadAppModal/DownloadAppModal';

import {
  PageContainer,
  ColumnContainer,
  InnerCardContainer,
  LoginHeader,
  LogoContainer,
  UllrLogo,
  InstructionText,
} from '../../../../../../src/login/styledComponents';

import { UllrLoginCard } from '../../../../../../src/components/ui/UllrCard';

import CONSTANTS from '../../../../../../src/constants';
import { ullrApi } from '../../../../../../src/services';

import iconSet from '../../../../../../src/assets/fonts/ullrFeatureIcons/selection.json';
import IcomoonReact from 'icomoon-react';
import background from '../../../../../../src/assets/images/contours.svg';
import '../../../../../../src/App.css';
import { toast } from 'react-toastify';

const FETCH_STATUSES = {
  INCOMPLETE: 'INCOMPLETE',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
};

interface PageProps {
  params: Promise<{
    sport: string;
    areaId: string;
  }>;
}

function PublicViewerContent({ params }: PageProps) {
  // Unwrap params using React.use()
  const { sport: urlSport, areaId } = use(params);

  // refs
  const mapRef = useRef<any>(null);

  // contexts
  const { COLORS } = useContext<ThemeContext>(ThemeContext);
  const searchParams = useSearchParams();
  const { sport, setSport } = useContext<SportContext>(SportContext);

  const queryParamHideOpenInAppPrompt = searchParams.get('hideOpenInAppPrompt') === 'true';
  const shouldShowDownloadPrompt = !queryParamHideOpenInAppPrompt && isMobile;

  // state
  const [mapKey, setMapKey] = useState(Date.now());
  const [isDownloadAppModalVisible, setIsDownloadAppModalVisible] = useState(shouldShowDownloadPrompt);
  const [readyToFetch, setReadyToFetch] = useState(false);
  const [isFetchComplete, setIsFetchComplete] = useState(FETCH_STATUSES.INCOMPLETE);
  const [resort, setResort] = useState<any>(null);
  const [terrainParkFeatures, setTerrainParkFeatures] = useState<any[]>([]);
  const [trails, setTrails] = useState<any[]>([]);
  const [featuresHaveBeenFetched, setFeaturesHaveBeenFetched] = useState(false);
  const [trailsHaveBeenFetched, setTrailsHaveBeenFetched] = useState(false);

  const fetchFeaturesAndTrails = async () => {
    let newFeatures: any[] = [];
    let newTrails: any[] = [];
    const promises = [];
    setFeaturesHaveBeenFetched(false);
    setTrailsHaveBeenFetched(false);
    try {
      const resortPromise = ullrApi.resort
        .getResort({
          sport,
          areaId,
        })
        .then((resortData: any) => {
          setResort(resortData);
        })
        .catch(() => {
          toast.error('Error loading resort');
        });
      promises.push(resortPromise);
      const featurePromise = ullrApi.feature
        .getFeatures({
          sport,
          areaId,
        })
        .then((features: any[]) => {
          newFeatures = features;
        })
        .catch(() => {
          toast.error('Error loading features');
        });
      promises.push(featurePromise);
      const trailPromise = ullrApi.trail
        .getTrails({
          sport,
          areaId,
        })
        .then((trailsData: any[]) => {
          newTrails = trailsData;
        })
        .catch(() => {
          toast.error('Error loading trails');
        });
      promises.push(trailPromise);

      await Promise.all(promises);
      // filter for status
      newFeatures = newFeatures.filter((set) => Number(set.status) !== -2 && Number(set.status) !== -1);

      setTerrainParkFeatures(newFeatures);
      setTrails(newTrails);
      setFeaturesHaveBeenFetched(true);
      setTrailsHaveBeenFetched(true);
      setIsFetchComplete(FETCH_STATUSES.COMPLETE);

      setMapKey(Date.now()); // This is a hacky way to force the map to re-render when there are features and trails loaded
      // there's some timing issues otherwise where the map won't focus on the trails / features
    } catch (error) {
      setIsFetchComplete(FETCH_STATUSES.ERROR);
    }
  };

  const mapInitialViewState = useMemo(() => {
    if (resort) {
      return {
        latitude: resort.latitude,
        longitude: resort.longitude,
        bearing: resort.settings?.defaultMapBearing ?? 0,
      };
    }
    return null;
  }, [resort]);

  // hooks

  /**
   * This hook should execute first.
   * It sets the sport context to match what is in the URL
   */
  useEffect(() => {
    const upperCaseUrlSport = urlSport && urlSport.toUpperCase();
    if (upperCaseUrlSport !== sport) {
      setSport((CONSTANTS.SPORTS as any)[upperCaseUrlSport] || CONSTANTS.SPORTS.SNOW);
    }
    setReadyToFetch(true);
  }, [setSport, sport, urlSport]);

  /**
   * This hook should execute only after the above hook completes.
   */
  useEffect(() => {
    if (readyToFetch) {
      fetchFeaturesAndTrails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToFetch, sport]);

  const initialPrimaryEntity = sport === CONSTANTS.SPORTS.BIKE ? CONSTANTS.PRIMARY_ENTITY_OPTIONS.TRAILS : CONSTANTS.PRIMARY_ENTITY_OPTIONS.FEATURES;

  return (
    <>
      <PageContainer
        style={{
          backgroundImage: `url(${(background as any).src || background})`,
          paddingTop: 0,
        }}
      >
        {/* Success state */}
        {isFetchComplete !== FETCH_STATUSES.ERROR && (
          <div
            style={{
              flex: 1,
            }}
          >
            <FeatureMapList
              key={`${areaId}${sport}${mapKey}`}
              ref={mapRef}
              initialViewState={mapInitialViewState}
              initialPrimaryEntity={initialPrimaryEntity}
              showFeatures={!!(terrainParkFeatures && terrainParkFeatures.length)}
              showTrails={!!(trails && trails.length)}
              features={terrainParkFeatures}
              trails={trails}
              renderEmptyStateFeatures={() => {
                if (featuresHaveBeenFetched) {
                  return (
                    <EmptyStateView
                      backgroundColor={COLORS.TRANSPARENT}
                      title={'No features reported'}
                      downloadPromptText={'Download the Ullr mobile app to get notified when features are added!'}
                    />
                  );
                } else {
                  return null;
                }
              }}
              renderEmptyStateTrails={() => {
                if (trailsHaveBeenFetched) {
                  return (
                    <EmptyStateView
                      backgroundColor={COLORS.TRANSPARENT}
                      title={'No trails reported'}
                      downloadPromptText={"Download the Ullr mobile app to keep up with trails once they're added!"}
                    />
                  );
                } else {
                  return null;
                }
              }}
            />
          </div>
        )}

        {/* Error State */}
        {isFetchComplete === FETCH_STATUSES.ERROR && (
          <ColumnContainer>
            <LogoContainer>
              <IcomoonReact iconSet={iconSet} color={COLORS.DARK} size={50} icon="ullr-jump" />
              <UllrLogo>Ullr</UllrLogo>
            </LogoContainer>
            <UllrLoginCard
              style={{
                alignSelf: 'stretch',
              }}
            >
              <InnerCardContainer>
                <LoginHeader>Error Loading :(</LoginHeader>
                <InstructionText>Please make sure that you followed the correct link to get here</InstructionText>
                {/* First Row */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                  }}
                ></div>
              </InnerCardContainer>
            </UllrLoginCard>
          </ColumnContainer>
        )}
      </PageContainer>

      {isFetchComplete === FETCH_STATUSES.INCOMPLETE && <UllrLoadingIndicator absolute style={{ zIndex: 200 }} />}

      <DownloadAppModal isOpen={isDownloadAppModalVisible} close={() => setIsDownloadAppModalVisible(false)} />
    </>
  );
}

export default function PublicViewerPage({ params }: PageProps) {
  return (
    <Suspense>
      <PublicViewerContent params={params} />
    </Suspense>
  );
}
